import { useEffect, useState } from "react";
import { Check, Loader2, AlertCircle, Cloud } from "lucide-react";
import type { SaveStatus } from "@/hooks/useInvoiceSession";

interface SaveIndicatorProps {
  status: SaveStatus;
  lastSavedAt: number | null;
}

const formatRelative = (ts: number): string => {
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (seconds < 5) return "ahora mismo";
  if (seconds < 60) return `hace ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `hace ${hours} h`;
};

const SaveIndicator = ({ status, lastSavedAt }: SaveIndicatorProps) => {
  const [, force] = useState(0);

  // Refresh "hace N s" label every 15 s
  useEffect(() => {
    if (status !== "saved" || !lastSavedAt) return;
    const id = setInterval(() => force((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [status, lastSavedAt]);

  if (status === "idle" && !lastSavedAt) return null;

  if (status === "saving" || status === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Guardando…
      </span>
    );
  }

  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
        <AlertCircle className="h-3 w-3" />
        Error al guardar
      </span>
    );
  }

  if (lastSavedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" title={new Date(lastSavedAt).toLocaleString("es-ES")}>
        <Check className="h-3 w-3 text-emerald-600" />
        Guardado {formatRelative(lastSavedAt)}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Cloud className="h-3 w-3" />
      Sin cambios
    </span>
  );
};

export default SaveIndicator;
